import React, { useEffect, useMemo, useState } from "react";
import { format, eachDayOfInterval } from "date-fns";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart3,
  CalendarDays,
  Car,
  RefreshCw,
  Truck,
  TrendingUp,
  History,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import ActualPerformanceTab from "@/pages/ActualPerformanceTab";
import RangeDatePicker, {
  computeRangeForMode,
  type RangeMode as PickerRangeMode,
} from "@/components/RangeDatePicker";


type TrendWindow = "1m" | "3m" | "6m" | "12m";
type MetricMode =
  | "jobs"
  | "vehicles"
  | "jobsPerVehicle"
  | "equivJobs";


type LongJobMode = "all" | "exclude" | "only";

type DemandRow = {
  active_date: string;
  iso_dow: number;
  dow_label: string;
  hour_of_day: number;

  actual_jobs: number;
  actual_vehicles: number;
  actual_drivers: number;
  actual_jobs_per_vehicle: number;
  actual_equiv_jobs: number;

  avg_jobs_1m: number | null;
  avg_jobs_3m: number | null;
  avg_jobs_6m: number | null;
  avg_jobs_12m: number | null;

  avg_vehicles_1m: number | null;
  avg_vehicles_3m: number | null;
  avg_vehicles_6m: number | null;
  avg_vehicles_12m: number | null;

  avg_jobs_per_vehicle_1m?: number | null;
  avg_jobs_per_vehicle_3m?: number | null;
  avg_jobs_per_vehicle_6m?: number | null;
  avg_jobs_per_vehicle_12m?: number | null;

  avg_equiv_jobs_1m?: number | null;
  avg_equiv_jobs_3m?: number | null;
  avg_equiv_jobs_6m?: number | null;
  avg_equiv_jobs_12m?: number | null;
};

type VehicleTypeRow = {
  active_date: string;
  hour_of_day: number;
  vehicle_type: string;
  active_jobs: number;
  active_vehicles: number;
  active_drivers: number;
  jobs_per_vehicle: number;
  equivalent_full_hour_jobs: number;
};

type Props = {
  selectedDate: Date;
  defaultFleet?: string | null;
  onOpenDayOverlay?: (dateStr: string) => void;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatHour = (hour: number) => {
  if (hour === 0) return "12a";
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return "12p";
  return `${hour - 12}p`;
};

const safeNum = (value: number | null | undefined) => Number(value || 0);

const WINDOW_COLORS: Record<TrendWindow, string> = {
  "1m": "#60a5fa",
  "3m": "#34d399",
  "6m": "#f59e0b",
  "12m": "#ef4444",
};

const METRIC_LABEL: Record<MetricMode, string> = {
  jobs: "Jobs",
  vehicles: "Vehicles",
  jobsPerVehicle: "Jobs / Vehicle",
  equivJobs: "Equivalent Full-Hour Jobs",
};

const RosterDemandTab: React.FC<Props> = ({
  selectedDate,
  defaultFleet = null,
  onOpenDayOverlay,
}) => {
  const [loading, setLoading] = useState(false);
  const [fleet, setFleet] = useState<string>(defaultFleet || "");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>("All");
  const [metricMode, setMetricMode] = useState<MetricMode>("jobs");
  const [longJobMode, setLongJobMode] = useState<LongJobMode>("all");
  const [selectedWindows, setSelectedWindows] = useState<TrendWindow[]>([
    "1m",
    "3m",
    "6m",
    "12m",
  ]);

  // Local date picker state (Day/Week/Month), seeded from parent's selectedDate
  const [demandAnchor, setDemandAnchor] = useState<Date>(selectedDate);
  const [demandPickerMode, setDemandPickerMode] =
    useState<PickerRangeMode>("day");

  // When parent changes the selected day (e.g. new week in calendar), resync
  useEffect(() => {
    setDemandAnchor(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate.toDateString()]);

  const [rows, setRows] = useState<DemandRow[]>([]);
  const [vehicleTypeRows, setVehicleTypeRows] = useState<VehicleTypeRow[]>([]);
  const [distinctJobs, setDistinctJobs] = useState<number>(0);
  const [rangeDayCount, setRangeDayCount] = useState<number>(1);

  const { start: rangeStart, end: rangeEnd, label: rangeLabel } = useMemo(
    () => computeRangeForMode(demandPickerMode, demandAnchor),
    [demandPickerMode, demandAnchor]
  );

  const rangeStartStr = format(rangeStart, "yyyy-MM-dd");
  const rangeEndStr = format(rangeEnd, "yyyy-MM-dd");

  const loadData = async () => {
    setLoading(true);
    try {
      const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });
      const dayStrs = days.map((d) => format(d, "yyyy-MM-dd"));
      setRangeDayCount(dayStrs.length);

      // Fan out: for each day in the range, pull the three RPCs. For Day mode
      // this is just one iteration so behaviour matches the old code.
      const perDayResults = await Promise.all(
        dayStrs.map(async (dStr) => {
          const [overlayRes, vehicleTypeRes, distinctRes] = await Promise.all([
            supabase.rpc("rpc_taxicaller_day_overlay", {
              p_selected_date: dStr,
              p_fleet: fleet || null,
              p_long_job_mode: longJobMode,
            }),
            supabase.rpc("rpc_taxicaller_day_overlay_by_vehicle_type", {
              p_selected_date: dStr,
              p_fleet: fleet || null,
              p_long_job_mode: longJobMode,
            }),
            supabase.rpc("rpc_taxicaller_day_distinct_job_count", {
              p_selected_date: dStr,
              p_fleet: fleet || null,
              p_long_job_mode: longJobMode,
            }),
          ]);

          if (overlayRes.error) throw overlayRes.error;
          if (vehicleTypeRes.error) throw vehicleTypeRes.error;
          if (distinctRes.error) throw distinctRes.error;

          const distinctData = (distinctRes.data || []) as Array<{
            distinct_jobs: number | string;
          }>;
          const first = distinctData[0];
          return {
            overlay: (overlayRes.data || []) as DemandRow[],
            byType: (vehicleTypeRes.data || []) as VehicleTypeRow[],
            distinct: first ? Number(first.distinct_jobs || 0) : 0,
          };
        })
      );

      // Combine: keep all overlay rows so chart builder can aggregate per hour
      const combinedOverlay: DemandRow[] = [];
      const combinedByType: VehicleTypeRow[] = [];
      let totalDistinct = 0;

      for (const r of perDayResults) {
        combinedOverlay.push(...r.overlay);
        combinedByType.push(...r.byType);
        totalDistinct += r.distinct;
      }

      setRows(combinedOverlay);
      setVehicleTypeRows(combinedByType);
      setDistinctJobs(totalDistinct);
    } catch (error) {
      console.error("Failed to load demand data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStartStr, rangeEndStr, fleet, longJobMode]);

  const isRange = demandPickerMode !== "day";

  const availableVehicleTypes = useMemo(() => {
    const set = new Set<string>();
    for (const row of vehicleTypeRows) {
      set.add(row.vehicle_type || "Unknown");
    }
    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [vehicleTypeRows]);

  const filteredVehicleTypeRows = useMemo(() => {
    if (vehicleTypeFilter === "All") return vehicleTypeRows;
    return vehicleTypeRows.filter(
      (r) => (r.vehicle_type || "Unknown") === vehicleTypeFilter,
    );
  }, [vehicleTypeRows, vehicleTypeFilter]);

  // Aggregate per hour-of-day across the selected range
  const chartData = useMemo(() => {
    // For each hour, accumulate sums and counts so we can sum actuals and
    // average trend windows (per hour-of-day over the range).
    type Accum = {
      hour_of_day: number;
      hour_label: string;
      actual_jobs_sum: number;
      actual_vehicles_sum: number;
      actual_vehicles_peak: number;
      actual_equiv_sum: number;
      actual_jpv_sum: number;
      actual_samples: number;

      avg_jobs: Record<TrendWindow, { sum: number; n: number }>;
      avg_vehicles: Record<TrendWindow, { sum: number; n: number }>;
      avg_jpv: Record<TrendWindow, { sum: number; n: number }>;
      avg_equiv: Record<TrendWindow, { sum: number; n: number }>;
    };

    const makeEmptyWindows = () => ({
      "1m": { sum: 0, n: 0 },
      "3m": { sum: 0, n: 0 },
      "6m": { sum: 0, n: 0 },
      "12m": { sum: 0, n: 0 },
    });

    const map = new Map<number, Accum>();
    for (const hour of HOURS) {
      map.set(hour, {
        hour_of_day: hour,
        hour_label: formatHour(hour),
        actual_jobs_sum: 0,
        actual_vehicles_sum: 0,
        actual_vehicles_peak: 0,
        actual_equiv_sum: 0,
        actual_jpv_sum: 0,
        actual_samples: 0,
        avg_jobs: makeEmptyWindows(),
        avg_vehicles: makeEmptyWindows(),
        avg_jpv: makeEmptyWindows(),
        avg_equiv: makeEmptyWindows(),
      });
    }

    const addWindow = (
      target: { sum: number; n: number },
      value: number | null | undefined
    ) => {
      if (value === null || value === undefined) return;
      target.sum += Number(value);
      target.n += 1;
    };

    // Choose source rows for actuals (vehicle-type filtered or all)
    if (vehicleTypeFilter !== "All") {
      // Aggregate actuals from filteredVehicleTypeRows (no trend windows there)
      for (const r of filteredVehicleTypeRows) {
        const entry = map.get(r.hour_of_day);
        if (!entry) continue;
        entry.actual_jobs_sum += safeNum(r.active_jobs);
        entry.actual_vehicles_sum += safeNum(r.active_vehicles);
        entry.actual_vehicles_peak = Math.max(
          entry.actual_vehicles_peak,
          safeNum(r.active_vehicles)
        );
        entry.actual_equiv_sum += safeNum(r.equivalent_full_hour_jobs);
        entry.actual_jpv_sum += safeNum(r.jobs_per_vehicle);
        entry.actual_samples += 1;
      }
      // Pull trend windows from unfiltered overlay rows (same hour-of-day)
      for (const r of rows) {
        const entry = map.get(r.hour_of_day);
        if (!entry) continue;
        addWindow(entry.avg_jobs["1m"], r.avg_jobs_1m);
        addWindow(entry.avg_jobs["3m"], r.avg_jobs_3m);
        addWindow(entry.avg_jobs["6m"], r.avg_jobs_6m);
        addWindow(entry.avg_jobs["12m"], r.avg_jobs_12m);
        addWindow(entry.avg_vehicles["1m"], r.avg_vehicles_1m);
        addWindow(entry.avg_vehicles["3m"], r.avg_vehicles_3m);
        addWindow(entry.avg_vehicles["6m"], r.avg_vehicles_6m);
        addWindow(entry.avg_vehicles["12m"], r.avg_vehicles_12m);
        addWindow(entry.avg_jpv["1m"], r.avg_jobs_per_vehicle_1m);
        addWindow(entry.avg_jpv["3m"], r.avg_jobs_per_vehicle_3m);
        addWindow(entry.avg_jpv["6m"], r.avg_jobs_per_vehicle_6m);
        addWindow(entry.avg_jpv["12m"], r.avg_jobs_per_vehicle_12m);
        addWindow(entry.avg_equiv["1m"], r.avg_equiv_jobs_1m);
        addWindow(entry.avg_equiv["3m"], r.avg_equiv_jobs_3m);
        addWindow(entry.avg_equiv["6m"], r.avg_equiv_jobs_6m);
        addWindow(entry.avg_equiv["12m"], r.avg_equiv_jobs_12m);
      }
    } else {
      for (const r of rows) {
        const entry = map.get(r.hour_of_day);
        if (!entry) continue;
        entry.actual_jobs_sum += safeNum(r.actual_jobs);
        entry.actual_vehicles_sum += safeNum(r.actual_vehicles);
        entry.actual_vehicles_peak = Math.max(
          entry.actual_vehicles_peak,
          safeNum(r.actual_vehicles)
        );
        entry.actual_equiv_sum += safeNum(r.actual_equiv_jobs);
        entry.actual_jpv_sum += safeNum(r.actual_jobs_per_vehicle);
        entry.actual_samples += 1;

        addWindow(entry.avg_jobs["1m"], r.avg_jobs_1m);
        addWindow(entry.avg_jobs["3m"], r.avg_jobs_3m);
        addWindow(entry.avg_jobs["6m"], r.avg_jobs_6m);
        addWindow(entry.avg_jobs["12m"], r.avg_jobs_12m);
        addWindow(entry.avg_vehicles["1m"], r.avg_vehicles_1m);
        addWindow(entry.avg_vehicles["3m"], r.avg_vehicles_3m);
        addWindow(entry.avg_vehicles["6m"], r.avg_vehicles_6m);
        addWindow(entry.avg_vehicles["12m"], r.avg_vehicles_12m);
        addWindow(entry.avg_jpv["1m"], r.avg_jobs_per_vehicle_1m);
        addWindow(entry.avg_jpv["3m"], r.avg_jobs_per_vehicle_3m);
        addWindow(entry.avg_jpv["6m"], r.avg_jobs_per_vehicle_6m);
        addWindow(entry.avg_jpv["12m"], r.avg_jobs_per_vehicle_12m);
        addWindow(entry.avg_equiv["1m"], r.avg_equiv_jobs_1m);
        addWindow(entry.avg_equiv["3m"], r.avg_equiv_jobs_3m);
        addWindow(entry.avg_equiv["6m"], r.avg_equiv_jobs_6m);
        addWindow(entry.avg_equiv["12m"], r.avg_equiv_jobs_12m);
      }
    }

    // Build final rows — in day mode the "sum" === single-day actual;
    // in range mode we show per-day average for actuals (so chart scales stay
    // comparable to trend-line averages which are per-day averages too).
    return Array.from(map.values())
      .sort((a, b) => a.hour_of_day - b.hour_of_day)
      .map((a) => {
        const divisor = isRange ? rangeDayCount : 1;
        const actual_jobs = a.actual_jobs_sum / divisor;
        const actual_vehicles = isRange
          ? a.actual_vehicles_sum / divisor
          : a.actual_vehicles_sum;
        const actual_equiv = a.actual_equiv_sum / divisor;
        const actual_jpv =
          a.actual_samples > 0 ? a.actual_jpv_sum / a.actual_samples : 0;

        const winAvg = (w: { sum: number; n: number }) =>
          w.n > 0 ? w.sum / w.n : 0;

        return {
          hour_of_day: a.hour_of_day,
          hour_label: a.hour_label,
          actual_jobs,
          actual_vehicles,
          actual_equiv_jobs: actual_equiv,
          actual_jobs_per_vehicle: actual_jpv,

          avg_jobs_1m: winAvg(a.avg_jobs["1m"]),
          avg_jobs_3m: winAvg(a.avg_jobs["3m"]),
          avg_jobs_6m: winAvg(a.avg_jobs["6m"]),
          avg_jobs_12m: winAvg(a.avg_jobs["12m"]),

          avg_vehicles_1m: winAvg(a.avg_vehicles["1m"]),
          avg_vehicles_3m: winAvg(a.avg_vehicles["3m"]),
          avg_vehicles_6m: winAvg(a.avg_vehicles["6m"]),
          avg_vehicles_12m: winAvg(a.avg_vehicles["12m"]),

          avg_jobs_per_vehicle_1m: winAvg(a.avg_jpv["1m"]),
          avg_jobs_per_vehicle_3m: winAvg(a.avg_jpv["3m"]),
          avg_jobs_per_vehicle_6m: winAvg(a.avg_jpv["6m"]),
          avg_jobs_per_vehicle_12m: winAvg(a.avg_jpv["12m"]),

          avg_equiv_jobs_1m: winAvg(a.avg_equiv["1m"]),
          avg_equiv_jobs_3m: winAvg(a.avg_equiv["3m"]),
          avg_equiv_jobs_6m: winAvg(a.avg_equiv["6m"]),
          avg_equiv_jobs_12m: winAvg(a.avg_equiv["12m"]),
        };
      });
  }, [rows, filteredVehicleTypeRows, vehicleTypeFilter, isRange, rangeDayCount]);

  const actualKey = useMemo(() => {
    switch (metricMode) {
      case "jobs":
        return "actual_jobs";
      case "vehicles":
        return "actual_vehicles";
      case "jobsPerVehicle":
        return "actual_jobs_per_vehicle";
      case "equivJobs":
        return "actual_equiv_jobs";
    }
  }, [metricMode]);

  const trendKeyFor = (window: TrendWindow) => {
    switch (metricMode) {
      case "jobs":
        return `avg_jobs_${window}`;
      case "vehicles":
        return `avg_vehicles_${window}`;
      case "jobsPerVehicle":
        return `avg_jobs_per_vehicle_${window}`;
      case "equivJobs":
        return `avg_equiv_jobs_${window}`;
    }
  };

  const toggleWindow = (window: TrendWindow) => {
    setSelectedWindows((prev) =>
      prev.includes(window)
        ? prev.filter((w) => w !== window)
        : [...prev, window],
    );
  };

  const totals = useMemo(() => {
    const totalJobs = chartData.reduce((sum, r) => sum + r.actual_jobs, 0);
    const peakHour = chartData.reduce(
      (best, r) => (r.actual_jobs > best.actual_jobs ? r : best),
      chartData[0] || { actual_jobs: 0, hour_label: "-" },
    );
    const peakVehicles = chartData.reduce(
      (max, r) => Math.max(max, r.actual_vehicles),
      0,
    );
    const avgJobsPerVehicle =
      chartData.length > 0
        ? chartData.reduce((sum, r) => sum + r.actual_jobs_per_vehicle, 0) /
          chartData.length
        : 0;

    return { totalJobs, peakHour, peakVehicles, avgJobsPerVehicle };
  }, [chartData]);

  const overviewTitle = useMemo(() => {
    if (demandPickerMode === "day") {
      return `Demand Overview — ${format(demandAnchor, "EEE")} ${formatDate(demandAnchor)}`;
    }
    if (demandPickerMode === "week") {
      return `Demand Overview — Week of ${rangeLabel}`;
    }
    return `Demand Overview — ${rangeLabel}`;
  }, [demandPickerMode, demandAnchor, rangeLabel]);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="demand" className="w-full">
        <TabsList className="bg-gray-800 border border-gray-700">
          <TabsTrigger value="demand" className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            Demand Trends
          </TabsTrigger>
          <TabsTrigger value="actual" className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" />
            Actual Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="demand" className="mt-4 space-y-4">

      {/* Roster-style date picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <RangeDatePicker
          mode={demandPickerMode}
          onModeChange={(m) => setDemandPickerMode(m)}
          anchor={demandAnchor}
          onAnchorChange={setDemandAnchor}
        />
        <div className="text-xs text-gray-400 ml-2">
          {isRange
            ? `Showing per-hour averages aggregated across ${rangeDayCount} day${rangeDayCount === 1 ? "" : "s"} · totals summed below.`
            : "Demand trends are shown for the selected day · use Week/Month to aggregate."}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" />
            {overviewTitle}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Fleet</Label>
              <Input
                value={fleet}
                onChange={(e) => setFleet(e.target.value)}
                placeholder="All fleets"
                className="h-9"
              />
            </div>

            <div>
              <Label className="text-xs">Vehicle Type</Label>
              <Select
                value={vehicleTypeFilter}
                onValueChange={setVehicleTypeFilter}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableVehicleTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Metric</Label>
              <Select
                value={metricMode}
                onValueChange={(v) => setMetricMode(v as MetricMode)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jobs">Jobs</SelectItem>
                  <SelectItem value="vehicles">Vehicles</SelectItem>
                  <SelectItem value="jobsPerVehicle">Jobs / Vehicle</SelectItem>
                  <SelectItem value="equivJobs">Equivalent Jobs</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Long Jobs</Label>
              <Select
                value={longJobMode}
                onValueChange={(v) => setLongJobMode(v as LongJobMode)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Include All</SelectItem>
                  <SelectItem value="exclude">Exclude Long Jobs</SelectItem>
                  <SelectItem value="only">Only Long Jobs</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                onClick={loadData}
                disabled={loading}
                className="h-9"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              {onOpenDayOverlay && demandPickerMode === "day" && (
                <Button
                  variant="secondary"
                  onClick={() => onOpenDayOverlay(rangeStartStr)}
                  className="h-9"
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Day View
                </Button>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground mr-1">
              Trend windows:
            </span>
            {(["1m", "3m", "6m", "12m"] as TrendWindow[]).map((w) => {
              const active = selectedWindows.includes(w);
              return (
                <Badge
                  key={w}
                  variant={active ? "default" : "outline"}
                  className="cursor-pointer"
                  style={
                    active
                      ? { backgroundColor: WINDOW_COLORS[w], borderColor: WINDOW_COLORS[w] }
                      : undefined
                  }
                  onClick={() => toggleWindow(w)}
                >
                  {w.toUpperCase()}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">
                  {isRange ? "Total Jobs (range)" : "Total Jobs"}
                </div>
                <div className="text-2xl font-semibold">{distinctJobs}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {isRange
                    ? `Distinct job IDs across ${rangeDayCount} days`
                    : "Distinct job IDs"}
                </div>
              </div>
              <BarChart3 className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">
                  {isRange ? "Avg Job-Hours / Day" : "Active Job-Hours"}
                </div>
                <div className="text-2xl font-semibold">
                  {totals.totalJobs.toFixed(isRange ? 1 : 0)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {isRange
                    ? "Average hourly activity per day"
                    : "Sum of hourly activity"}
                </div>
              </div>
              <BarChart3 className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">Peak Hour</div>
                <div className="text-2xl font-semibold">
                  {totals.peakHour?.hour_label || "-"}
                </div>
              </div>
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">
                  {isRange ? "Avg Peak Vehicles" : "Peak Vehicles"}
                </div>
                <div className="text-2xl font-semibold">
                  {isRange
                    ? totals.peakVehicles.toFixed(1)
                    : totals.peakVehicles}
                </div>
              </div>
              <Truck className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">
                  Avg Jobs / Vehicle
                </div>
                <div className="text-2xl font-semibold">
                  {totals.avgJobsPerVehicle.toFixed(2)}
                </div>
              </div>
              <Car className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {METRIC_LABEL[metricMode]} by Hour — Actual vs Trend
            {isRange && (
              <span className="text-xs text-muted-foreground font-normal ml-2">
                (per-day averages across {rangeDayCount} days)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[420px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour_label" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey={actualKey}
                  name={`Actual ${METRIC_LABEL[metricMode]}${isRange ? " (avg/day)" : ""}`}
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
                {selectedWindows.map((w) => (
                  <Line
                    key={w}
                    type="monotone"
                    dataKey={trendKeyFor(w)}
                    name={`${w.toUpperCase()} Avg`}
                    stroke={WINDOW_COLORS[w]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle type breakdown */}
      {vehicleTypeFilter === "All" && vehicleTypeRows.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="h-4 w-4" />
              Vehicle Type Breakdown
              {isRange && (
                <span className="text-xs text-muted-foreground font-normal ml-2">
                  (summed across {rangeDayCount} days)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 pr-4">Vehicle Type</th>
                    <th className="py-2 pr-4">Total Jobs</th>
                    <th className="py-2 pr-4">Peak Vehicles</th>
                    <th className="py-2 pr-4">Equiv. Jobs</th>
                    <th className="py-2 pr-4">Avg Jobs/Vehicle</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const byType = new Map<
                      string,
                      {
                        jobs: number;
                        peakVehicles: number;
                        equiv: number;
                        jpvValues: number[];
                      }
                    >();
                    for (const row of vehicleTypeRows) {
                      const key = row.vehicle_type || "Unknown";
                      const entry = byType.get(key) || {
                        jobs: 0,
                        peakVehicles: 0,
                        equiv: 0,
                        jpvValues: [],
                      };
                      entry.jobs += safeNum(row.active_jobs);
                      entry.peakVehicles = Math.max(
                        entry.peakVehicles,
                        safeNum(row.active_vehicles),
                      );
                      entry.equiv += safeNum(row.equivalent_full_hour_jobs);
                      entry.jpvValues.push(safeNum(row.jobs_per_vehicle));
                      byType.set(key, entry);
                    }
                    return Array.from(byType.entries())
                      .sort((a, b) => b[1].jobs - a[1].jobs)
                      .map(([type, data]) => {
                        const avgJpv =
                          data.jpvValues.length > 0
                            ? data.jpvValues.reduce((s, v) => s + v, 0) /
                              data.jpvValues.length
                            : 0;
                        return (
                          <tr key={type} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium">{type}</td>
                            <td className="py-2 pr-4">{data.jobs.toFixed(0)}</td>
                            <td className="py-2 pr-4">{data.peakVehicles}</td>
                            <td className="py-2 pr-4">
                              {data.equiv.toFixed(1)}
                            </td>
                            <td className="py-2 pr-4">{avgJpv.toFixed(2)}</td>
                          </tr>
                        );
                      });
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="actual" className="mt-4">
          <ActualPerformanceTab
            initialDate={demandAnchor}
            fleet={fleet || null}
          />
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default RosterDemandTab;
